import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import actions from '../redux/actions';
import { OperationStatus } from '../types/OperationStatus';
import { Notice } from '../ui';

type Field = 'currentPassword' | 'newPassword' | 'newPasswordAgain';

const fields: { name: Field; label: string }[] = [
  { name: 'currentPassword', label: 'Current password' },
  { name: 'newPassword', label: 'New password' },
  { name: 'newPasswordAgain', label: 'Repeat new password' },
];

const empty = { currentPassword: '', newPassword: '', newPasswordAgain: '' };

/* "Change password" button + dialog (profile pages). Open state lives in the store. */
const PasswordChangeModal: React.FC = () => {
  const dispatch = useDispatch();
  const open: boolean = useSelector((store: any) => store.changePasswordModal.open);
  const status = useSelector((store: any) => store.passwordChangeStatus.status);
  const serverError: string = useSelector((store: any) => store.passwordChangeStatus.errorMessage);

  const [values, setValues] = useState(empty);
  const [visible, setVisible] = useState<Record<Field, boolean>>({ currentPassword: false, newPassword: false, newPasswordAgain: false });
  const [errors, setErrors] = useState<Set<Field>>(new Set());
  const [errorMessage, setErrorMessage] = useState('');

  const reset = () => {
    setValues(empty);
    setVisible({ currentPassword: false, newPassword: false, newPasswordAgain: false });
    setErrors(new Set());
    setErrorMessage('');
  };

  const close = () => {
    reset();
    dispatch(actions.changePasswordModalClose());
  };

  useEffect(() => {
    if (status === OperationStatus.success) {
      close();
    } else if (status === OperationStatus.fail) {
      setErrorMessage(serverError);
    }
  }, [status]);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors = new Set<Field>(fields.filter((field) => !values[field.name]).map((field) => field.name));

    if (values.newPassword.length < 8) {
      nextErrors.add('newPassword');
      setErrors(nextErrors);
      return setErrorMessage('Password need to be at least 8 characters');
    }
    if (values.newPassword !== values.newPasswordAgain) {
      nextErrors.add('newPasswordAgain');
      setErrors(nextErrors);
      return setErrorMessage("Passwords don't match");
    }

    setErrors(nextErrors);
    setErrorMessage('');
    if (!nextErrors.size) {
      dispatch(actions.changePassword(values.currentPassword, values.newPassword));
    }
  };

  return (
    <>
      <Button variant="outlined" onClick={() => dispatch(actions.changePasswordModalOpen())}>
        Change password
      </Button>
      <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
        <form onSubmit={onSubmit}>
          <DialogTitle>Change password</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
            {errorMessage && <Notice severity="error">{errorMessage}</Notice>}
            {fields.map((field) => (
              <TextField
                key={field.name}
                name={field.name}
                label={field.label}
                type={visible[field.name] ? 'text' : 'password'}
                value={values[field.name]}
                error={errors.has(field.name)}
                onChange={(event) => setValues({ ...values, [field.name]: event.target.value })}
                onBlur={(event) => {
                  const next = new Set(errors);
                  event.target.value ? next.delete(field.name) : next.add(field.name);
                  setErrors(next);
                }}
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        tabIndex={-1}
                        aria-label={visible[field.name] ? 'Hide password' : 'Show password'}
                        onClick={() => setVisible({ ...visible, [field.name]: !visible[field.name] })}
                        edge="end"
                      >
                        {visible[field.name] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={close}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={status === OperationStatus.processing}>
              Change password
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default PasswordChangeModal;
